/**
 * <date-range-picker> Web Component
 *
 * A date range picker for check-in/check-out date selection.
 * Modeled after Campspot's date picker with two-click selection
 * and dual-month display.
 *
 * Usage:
 *   <date-range-picker></date-range-picker>
 *   <date-range-picker show-nights></date-range-picker>
 *
 * Attributes:
 *   - show-nights: Show the nights count display
 *   - allow-past: Allow selection of past dates (for admin/reports)
 *   - single: One click selects that day and closes. End stays empty.
 *   - headless: Hide the field chrome. openDropdown(anchor) positions the
 *     calendar under the caller's own button. The host stays in the
 *     document (a display:none host would hide the fixed dropdown).
 *
 * Events:
 *   - datechange: Fired when dates change
 *     detail: { start: Date, end: Date }
 *   - open: Fired when calendar opens
 *   - close: Fired when calendar closes
 *
 * Form Integration:
 *   Uses ElementInternals for native form participation.
 *   Submits 'start-date' and 'end-date' as YYYY-MM-DD strings.
 */
class DateRangePickerElement extends HTMLElement {
  static formAssociated = true;
  static observedAttributes = ['show-nights', 'allow-past', 'data-start-label', 'data-end-label'];

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.internals = this.attachInternals();

    // State
    this.startDate = null;
    this.endDate = null;
    this.isOpen = false;
    this.currentMonth = new Date();
    this.selectingEnd = false; // false = selecting start, true = selecting end

    // Bound handlers for cleanup
    this._boundScrollHandler = this._onScroll.bind(this);
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.updateNightsVisibility();
  }

  disconnectedCallback() {
    // Clean up scroll listener if dropdown was open
    window.removeEventListener('scroll', this._boundScrollHandler, true);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'show-nights') {
      this.updateNightsVisibility();
    } else if (name === 'data-start-label' || name === 'data-end-label') {
      this.updateFieldDisplay();
    }
  }

  updateNightsVisibility() {
    const nightsDisplay = this.shadowRoot.querySelector('.nights-display');
    if (nightsDisplay) {
      const showNights = this.hasAttribute('show-nights');
      nightsDisplay.classList.toggle('hidden', !showNights);
    }
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        /* ===== CSS Custom Properties (Themeable) ===== */
        :host {
          /* Field styling */
          --drp-field-bg: var(--lb-surface, #fff);
          --drp-field-border: var(--lb-line, #ccc);
          --drp-field-border-hover: var(--lb-accent, #999);
          --drp-field-radius: 4px;
          --drp-field-padding: 12px;

          /* Text colors */
          --drp-text-color: var(--lb-text, #333);
          --drp-text-muted: var(--lb-muted, #666);
          --drp-text-placeholder: var(--lb-muted, #999);

          /* Primary/accent color */
          --drp-primary: var(--lb-accent, #007bff);
          --drp-primary-text: var(--lb-accent-fg, #fff);

          /* Selection & highlighting */
          --drp-selected-bg: var(--drp-primary);
          --drp-selected-text: var(--drp-primary-text);
          --drp-range-bg: var(--lb-selection-bg, #e6f0ff);
          --drp-range-preview-bg: var(--lb-surface-2, #f0f7ff);
          --drp-selecting-bg: var(--lb-selection-bg, #e6f0ff);

          /* Dropdown styling */
          --drp-dropdown-bg: var(--lb-surface, #fff);
          --drp-dropdown-border: var(--lb-line, #ccc);
          --drp-dropdown-shadow: rgba(0, 0, 0, 0.15);

          /* Button styling */
          --drp-button-bg: transparent;
          --drp-button-bg-hover: var(--lb-surface-2, #f5f5f5);
          --drp-button-border: var(--lb-line, #ddd);

          /* Separator/divider */
          --drp-separator-color: var(--lb-line, #eee);

          display: block;
          position: relative;
          font-family: inherit;
        }

        .field {
          flex: 1;
          display: flex;
          align-items: center;
          background: var(--drp-field-bg);
          border: 1px solid var(--drp-field-border);
          border-radius: var(--drp-field-radius);
          cursor: pointer;
        }

        .field:hover {
          border-color: var(--drp-field-border-hover);
        }

        .date-button {
          flex: 1;
          padding: var(--drp-field-padding);
          border: none;
          background: var(--drp-button-bg);
          font-size: 1rem;
          cursor: pointer;
          text-align: center;
          color: var(--drp-text-color);
          white-space: nowrap;
        }

        .date-button:hover {
          background: var(--drp-button-bg-hover);
        }

        .date-button.placeholder {
          color: var(--drp-text-placeholder);
        }

        .date-button.selected {
          color: var(--drp-primary);
          font-weight: 600;
        }

        .date-button.selecting {
          background: var(--drp-selecting-bg);
          border-bottom: 2px solid var(--drp-primary);
        }

        .container {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .nights-display {
          display: flex;
          align-items: baseline;
          gap: 4px;
          white-space: nowrap;
          color: var(--drp-text-muted);
        }

        .nights-display.hidden {
          display: none;
        }

        .nights-count {
          font-size: 1rem;
          font-weight: 600;
          color: var(--drp-text-color);
        }

        .nights-label {
          font-size: 0.875rem;
        }

        .separator {
          color: var(--drp-text-muted);
          padding: 0 8px;
        }

        .dropdown {
          display: none;
          position: fixed;
          background: var(--drp-dropdown-bg);
          border: 1px solid var(--drp-dropdown-border);
          border-radius: var(--drp-field-radius);
          box-shadow: 0 4px 12px var(--drp-dropdown-shadow);
          z-index: 10000;
          padding: 16px;
        }

        .dropdown.open {
          display: block;
        }

        /* ===== Dual Calendar Layout (Campspot-style) ===== */
        .calendars-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .nav-button {
          background: var(--drp-button-bg);
          border: 1px solid var(--drp-button-border);
          border-radius: var(--drp-field-radius);
          padding: 6px 10px;
          cursor: pointer;
          color: var(--drp-text-color);
          font-size: 1rem;
          line-height: 1;
        }

        .nav-button:hover {
          background: var(--drp-button-bg-hover);
        }

        .calendars-container {
          display: flex;
          gap: 24px;
        }

        .calendar {
          flex: 0 0 auto;
          width: 252px;  /* 7 cols × 36px */
        }

        /* Show second calendar by default */
        .calendar.second {
          display: block;
        }

        /* Hide second calendar on narrow viewports */
        @media (max-width: 600px) {
          .calendars-container {
            gap: 0;
          }
          .calendar.second {
            display: none;
          }
        }

        .calendar-title {
          font-weight: 600;
          color: var(--drp-text-color);
          text-align: center;
          margin-bottom: 8px;
          font-size: 0.95rem;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
        }

        .weekday {
          text-align: center;
          font-size: 0.7rem;
          color: var(--drp-text-muted);
          padding: 6px 0;
          font-weight: 500;
        }

        .day {
          display: flex;
          align-items: center;
          justify-content: center;
          aspect-ratio: 1;
          border: none;
          background: var(--drp-button-bg);
          cursor: pointer;
          border-radius: var(--drp-field-radius);
          color: var(--drp-text-color);
          font-size: 0.85rem;
        }

        .day:hover:not(.disabled):not(.other-month) {
          background: var(--drp-range-bg);
        }

        .day.other-month {
          visibility: hidden;
        }

        .day.disabled {
          color: var(--drp-text-placeholder);
          cursor: not-allowed;
          opacity: 0.5;
        }

        .day.today {
          font-weight: 600;
          box-shadow: inset 0 0 0 1px var(--drp-primary);
        }

        .day.start-date,
        .day.end-date {
          background: var(--drp-selected-bg);
          color: var(--drp-selected-text);
        }

        .day.in-range {
          background: var(--drp-range-bg);
          border-radius: 0;
        }

        .day.start-date {
          border-radius: var(--drp-field-radius) 0 0 var(--drp-field-radius);
        }

        .day.end-date {
          border-radius: 0 var(--drp-field-radius) var(--drp-field-radius) 0;
        }

        .day.start-date.end-date {
          border-radius: var(--drp-field-radius);
        }

        .day.preview-range {
          background: var(--drp-range-preview-bg);
        }

        .nights-preview {
          display: flex;
          justify-content: center;
          align-items: baseline;
          gap: 4px;
          padding: 12px;
          margin-top: 8px;
          border-top: 1px solid var(--drp-separator-color);
          font-size: 0.9rem;
          color: var(--drp-text-muted);
          min-height: 20px;
        }

        .nights-preview-count {
          font-weight: 600;
          color: var(--drp-primary);
        }

        .nights-preview.hidden {
          visibility: hidden;
        }

        :host([single]) .separator,
        :host([single]) .end-button,
        :host([single]) .nights-display,
        :host([single]) .nights-preview {
          display: none;
        }

        :host([single]) .day.start-date {
          border-radius: var(--drp-field-radius);
        }

        :host([headless]) {
          position: absolute;
          width: 0;
          height: 0;
          overflow: visible;
          pointer-events: none;
        }

        :host([headless]) .container {
          display: none;
        }

        :host([headless]) .dropdown {
          pointer-events: auto;
        }
      </style>

      <div class="container">
        <div class="field">
          <button type="button" class="date-button start-button placeholder">
            ${this.dataset.startLabel || 'Check In'}
          </button>
          <span class="separator">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </span>
          <button type="button" class="date-button end-button placeholder">
            ${this.dataset.endLabel || 'Check Out'}
          </button>
        </div>

        <div class="nights-display">
          <span class="nights-count">-</span>
          <span class="nights-label">nights</span>
        </div>
      </div>

      <div class="dropdown">
        <div class="calendars-header">
          <button type="button" class="nav-button prev-month">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <div style="flex: 1;"></div>
          <button type="button" class="nav-button next-month">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>

        <div class="calendars-container">
          <!-- First calendar (current month) -->
          <div class="calendar first">
            <div class="calendar-title first-month-title"></div>
            <div class="calendar-grid first-grid">
              <div class="weekday">Su</div>
              <div class="weekday">Mo</div>
              <div class="weekday">Tu</div>
              <div class="weekday">We</div>
              <div class="weekday">Th</div>
              <div class="weekday">Fr</div>
              <div class="weekday">Sa</div>
            </div>
          </div>

          <!-- Second calendar (next month) -->
          <div class="calendar second">
            <div class="calendar-title second-month-title"></div>
            <div class="calendar-grid second-grid">
              <div class="weekday">Su</div>
              <div class="weekday">Mo</div>
              <div class="weekday">Tu</div>
              <div class="weekday">We</div>
              <div class="weekday">Th</div>
              <div class="weekday">Fr</div>
              <div class="weekday">Sa</div>
            </div>
          </div>
        </div>

        <div class="nights-preview">
          <span class="nights-preview-count"></span>
          <span class="nights-preview-label"></span>
        </div>
      </div>
    `;

    this.updateMonthDisplay();
    this.renderCalendarDays();
  }

  attachEventListeners() {
    const startBtn = this.shadowRoot.querySelector('.start-button');
    const endBtn = this.shadowRoot.querySelector('.end-button');
    const prevBtn = this.shadowRoot.querySelector('.prev-month');
    const nextBtn = this.shadowRoot.querySelector('.next-month');

    // Click on start date button - edit start date
    startBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.selectingEnd = false;
      this.openDropdown();
    });

    // Click on end date button - edit end date (keep start date)
    endBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.selectingEnd = this.startDate !== null;
      this.openDropdown();
    });

    // Month navigation
    prevBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.currentMonth.setMonth(this.currentMonth.getMonth() - 1);
      this.updateMonthDisplay();
      this.renderCalendarDays();
    });

    nextBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.currentMonth.setMonth(this.currentMonth.getMonth() + 1);
      this.updateMonthDisplay();
      this.renderCalendarDays();
    });

    // Close on outside click
    // Use composedPath() to correctly detect clicks across Shadow DOM boundaries
    // (needed when component is nested inside another Shadow DOM host)
    document.addEventListener('click', (e) => {
      if (!e.composedPath().includes(this) && this.isOpen) {
        this.closeDropdown();
      }
    });
  }

  updateMonthDisplay() {
    const firstTitle = this.shadowRoot.querySelector('.first-month-title');
    const secondTitle = this.shadowRoot.querySelector('.second-month-title');
    const formatter = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
    });

    // First calendar shows currentMonth
    firstTitle.textContent = formatter.format(this.currentMonth);

    // Second calendar shows next month
    const nextMonth = new Date(this.currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    secondTitle.textContent = formatter.format(nextMonth);
  }

  renderCalendarDays() {
    // Render first calendar (current month)
    this._renderMonth(
      this.shadowRoot.querySelector('.first-grid'),
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth()
    );

    // Render second calendar (next month)
    const nextMonth = new Date(this.currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    this._renderMonth(this.shadowRoot.querySelector('.second-grid'), nextMonth.getFullYear(), nextMonth.getMonth());
  }

  _renderMonth(grid, year, month) {
    // Remove old day buttons (keep weekday headers)
    const existingDays = grid.querySelectorAll('.day');
    existingDays.forEach((day) => day.remove());

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // First day of month
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay();

    // Days in month
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Add empty cells for days before first of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      const emptyDay = document.createElement('div');
      emptyDay.className = 'day other-month';
      grid.appendChild(emptyDay);
    }

    // Add day buttons
    for (let day = 1; day <= daysInMonth; day++) {
      const dayBtn = document.createElement('button');
      dayBtn.type = 'button';
      dayBtn.className = 'day';
      dayBtn.textContent = day;

      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      dayBtn.dataset.date = date.toISOString().split('T')[0];

      // Today styling
      if (this.isSameDay(date, today)) {
        dayBtn.classList.add('today');
      }

      // Past dates are disabled (unless allow-past attribute is set)
      if (date < today && !this.hasAttribute('allow-past')) {
        dayBtn.classList.add('disabled');
      }

      // Apply selection styles
      if (this.startDate && this.isSameDay(date, this.startDate)) {
        dayBtn.classList.add('start-date');
      }
      if (this.endDate && this.isSameDay(date, this.endDate)) {
        dayBtn.classList.add('end-date');
      }
      if (this.startDate && this.endDate && date > this.startDate && date < this.endDate) {
        dayBtn.classList.add('in-range');
      }

      // Click handler for date selection (only for non-disabled dates)
      dayBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!dayBtn.classList.contains('disabled')) {
          this.handleDateClick(date);
        }
      });

      // Hover handler for range preview
      dayBtn.addEventListener('mouseenter', () => {
        if (this.startDate && !this.endDate && this.selectingEnd && !dayBtn.classList.contains('disabled')) {
          this.showRangePreview(date);
        }
      });

      grid.appendChild(dayBtn);
    }
  }

  handleDateClick(date) {
    if (this.hasAttribute('single')) {
      this.startDate = date;
      this.endDate = null;
      this.selectingEnd = false;
      this.updateFieldDisplay();
      this.updateSelectingIndicator();
      this.updateNightsDisplay();
      this.updateNightsPreview();
      this.renderCalendarDays();
      this.closeDropdown();
      this.emitChange();
      return;
    }

    if (!this.selectingEnd) {
      // We're selecting/changing the start date
      this.startDate = date;
      // If the new start is after existing end, clear the end
      if (this.endDate && date >= this.endDate) {
        this.endDate = null;
      }
      this.selectingEnd = true; // Now wait for end date
    } else {
      // We're selecting/changing the end date
      if (date >= this.startDate) {
        // Valid end date: set it and close
        this.endDate = date;
        this.selectingEnd = false;
        this.closeDropdown();
      } else {
        // Clicked before start date: treat as new start, keep waiting for end
        this.startDate = date;
        this.endDate = null;
        // selectingEnd stays true
      }
    }

    this.updateFieldDisplay();
    this.updateSelectingIndicator();
    this.updateNightsDisplay();
    this.updateNightsPreview();
    this.renderCalendarDays();
    this.emitChange();
  }

  showRangePreview(hoverDate) {
    const days = this.shadowRoot.querySelectorAll('.day');
    days.forEach((dayBtn) => {
      dayBtn.classList.remove('preview-range');

      if (dayBtn.dataset.date) {
        const date = new Date(dayBtn.dataset.date);
        if (this.startDate && date > this.startDate && date <= hoverDate) {
          dayBtn.classList.add('preview-range');
        }
      }
    });

    // Update nights preview in dropdown
    this.updateNightsPreview(hoverDate);
  }

  updateNightsPreview(hoverDate = null) {
    const preview = this.shadowRoot.querySelector('.nights-preview');
    const countEl = this.shadowRoot.querySelector('.nights-preview-count');
    const labelEl = this.shadowRoot.querySelector('.nights-preview-label');

    if (!preview || !this.hasAttribute('show-nights')) {
      if (preview) preview.classList.add('hidden');
      return;
    }

    // Show preview when we have a start date and are hovering or have an end date
    const targetDate = hoverDate || this.endDate;

    if (this.startDate && targetDate && targetDate >= this.startDate) {
      const diffTime = targetDate.getTime() - this.startDate.getTime();
      const nights = Math.round(diffTime / (1000 * 60 * 60 * 24));
      countEl.textContent = nights;
      labelEl.textContent = nights === 1 ? 'night' : 'nights';
      preview.classList.remove('hidden');
    } else if (this.selectingEnd && this.startDate) {
      // Waiting for hover/selection
      countEl.textContent = '';
      labelEl.textContent = 'Select check-out date';
      preview.classList.remove('hidden');
    } else {
      preview.classList.add('hidden');
    }
  }

  clearNightsPreview() {
    const preview = this.shadowRoot.querySelector('.nights-preview');
    if (preview) {
      preview.classList.add('hidden');
    }
  }

  updateFieldDisplay() {
    const startBtn = this.shadowRoot.querySelector('.start-button');
    const endBtn = this.shadowRoot.querySelector('.end-button');

    // attributeChangedCallback can fire before connectedCallback has
    // rendered the shadow tree (when initial attrs are set on the
    // element); in that case the buttons don't exist yet. The render
    // will pick up the latest values on its first pass, so just bail.
    if (!startBtn || !endBtn) return;

    const formatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

    if (this.startDate) {
      startBtn.textContent = formatter.format(this.startDate);
      startBtn.classList.remove('placeholder');
      startBtn.classList.add('selected');
    } else {
      startBtn.textContent = this.dataset.startLabel || 'Check In';
      startBtn.classList.add('placeholder');
      startBtn.classList.remove('selected');
    }

    if (this.endDate) {
      endBtn.textContent = formatter.format(this.endDate);
      endBtn.classList.remove('placeholder');
      endBtn.classList.add('selected');
    } else {
      endBtn.textContent = this.dataset.endLabel || 'Check Out';
      endBtn.classList.add('placeholder');
      endBtn.classList.remove('selected');
    }
  }

  /**
   * Open the calendar. Range mode calls this from the field buttons.
   * Headless callers pass the button the dropdown should sit under.
   * @param {Element} [anchor]
   */
  openDropdown(anchor) {
    const dropdown = this.shadowRoot.querySelector('.dropdown');
    if (!dropdown) return;
    if (anchor) this._anchorEl = anchor;
    if (this.hasAttribute('single')) this.selectingEnd = false;
    const alreadyOpen = this.isOpen;
    this.isOpen = true;
    dropdown.classList.add('open');
    this._positionDropdown();
    if (!alreadyOpen) {
      window.addEventListener('scroll', this._boundScrollHandler, true);
      this.dispatchEvent(new CustomEvent('open'));
    }
    this.updateSelectingIndicator();
    this.updateNightsPreview();
  }

  /**
   * Position the dropdown using fixed positioning relative to viewport.
   * This allows the dropdown to escape overflow:hidden containers.
   * @private
   */
  _positionDropdown() {
    const field = this.shadowRoot.querySelector('.field');
    const dropdown = this.shadowRoot.querySelector('.dropdown');
    const anchor = this._anchorEl && this._anchorEl.isConnected ? this._anchorEl : field;
    if (!anchor || !dropdown) return;

    const fieldRect = anchor.getBoundingClientRect();
    const dropdownHeight = dropdown.offsetHeight || 400; // Estimate if not yet rendered
    const dropdownWidth = dropdown.offsetWidth || 550;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const gap = 4; // Gap between field and dropdown

    // Default: position below the field
    let top = fieldRect.bottom + gap;
    let left = fieldRect.left;

    // If dropdown would go off bottom, flip to above
    if (top + dropdownHeight > viewportHeight && fieldRect.top > dropdownHeight + gap) {
      top = fieldRect.top - dropdownHeight - gap;
    }

    // If dropdown would go off right edge, align to right edge of field or viewport
    if (left + dropdownWidth > viewportWidth) {
      left = Math.max(8, viewportWidth - dropdownWidth - 8);
    }

    // Ensure not off left edge
    if (left < 8) {
      left = 8;
    }

    dropdown.style.top = `${top}px`;
    dropdown.style.left = `${left}px`;
  }

  /**
   * Handle scroll events - close dropdown when user scrolls.
   * @private
   */
  _onScroll() {
    if (this.isOpen) {
      this.closeDropdown();
    }
  }

  closeDropdown() {
    this.isOpen = false;
    this.shadowRoot.querySelector('.dropdown').classList.remove('open');
    this.updateSelectingIndicator(); // Clear indicator when closed
    // Remove scroll listener
    window.removeEventListener('scroll', this._boundScrollHandler, true);
    this.dispatchEvent(new CustomEvent('close'));
  }

  updateSelectingIndicator() {
    const startBtn = this.shadowRoot.querySelector('.start-button');
    const endBtn = this.shadowRoot.querySelector('.end-button');
    if (!startBtn || !endBtn) return;

    // Remove from both first
    startBtn.classList.remove('selecting');
    endBtn.classList.remove('selecting');

    // Only show indicator when dropdown is open
    if (this.isOpen) {
      if (this.selectingEnd) {
        endBtn.classList.add('selecting');
      } else {
        startBtn.classList.add('selecting');
      }
    }
  }

  updateNightsDisplay() {
    const nightsCount = this.shadowRoot.querySelector('.nights-count');
    const nightsLabel = this.shadowRoot.querySelector('.nights-label');

    if (this.startDate && this.endDate) {
      const diffTime = this.endDate.getTime() - this.startDate.getTime();
      const nights = Math.round(diffTime / (1000 * 60 * 60 * 24));
      nightsCount.textContent = nights;
      nightsLabel.textContent = nights === 1 ? 'night' : 'nights';
    } else {
      nightsCount.textContent = '-';
      nightsLabel.textContent = 'nights';
    }
  }

  isSameDay(date1, date2) {
    return date1.toISOString().split('T')[0] === date2.toISOString().split('T')[0];
  }

  emitChange() {
    // Update form value via ElementInternals
    if (this.startDate && this.endDate) {
      const formData = new FormData();
      formData.append('start-date', this.startDate.toISOString().split('T')[0]);
      formData.append('end-date', this.endDate.toISOString().split('T')[0]);
      this.internals.setFormValue(formData);
    }

    // Dispatch change event
    this.dispatchEvent(
      new CustomEvent('datechange', {
        detail: {
          start: this.startDate,
          end: this.endDate,
        },
      })
    );
  }

  // Public API
  get value() {
    return {
      start: this.startDate,
      end: this.endDate,
    };
  }

  set value(val) {
    if (val && val.start) this.startDate = new Date(val.start);
    if (val && val.end) this.endDate = new Date(val.end);
    this.updateFieldDisplay();
    this.updateNightsDisplay();
    this.renderCalendarDays();
  }

  /**
   * Get start date as ISO string (YYYY-MM-DD)
   * @returns {string|null}
   */
  get startDateValue() {
    return this.startDate ? this.startDate.toISOString().split('T')[0] : null;
  }

  /**
   * Get end date as ISO string (YYYY-MM-DD)
   * @returns {string|null}
   */
  get endDateValue() {
    return this.endDate ? this.endDate.toISOString().split('T')[0] : null;
  }

  /**
   * Reset the picker to initial state
   */
  reset() {
    this.startDate = null;
    this.endDate = null;
    this.selectingEnd = false;
    this.updateFieldDisplay();
    this.updateNightsDisplay();
    this.renderCalendarDays();
  }
}

export { DateRangePickerElement };
